<template>
  <!--
    SP/PC 出し分けモード（spImageUrl と pcImageUrl の両方を渡したとき）。
    picture は display:contents（class="contents"）でレイアウトに影響させず、
    幅・object-fit・アニメ等のクラスはインナー img（props.class）に集約する。
    JS(isSP) ではなく media でブラウザに出し分けさせるため SP でのチラつき・二重DLが起きない。
  -->
  <picture v-if="isPictureMode" class="contents">
    <source media="(min-width: 768px)" :srcset="optimizedPcImageUrl" />
    <img decoding="async" :alt="props.alt" :class="props.class" :fetchpriority="props.fetchpriority" :loading="props.loading" :src="optimizedSpImageUrl" />
  </picture>
  <!-- 単一画像モード -->
  <img v-else decoding="async" :alt="props.alt" :class="props.class" :fetchpriority="props.fetchpriority" :loading="props.loading" :src="optimizedImageUrl" />
</template>
<script setup lang="ts">
import { useOptimizedImageUrl } from '@/wabunka-src/common/composables/general/useOptimizedImageUrl';
import type { OptimizeImageOptions } from '@/wabunka-src/common/utils/image/imageOptimizer';

import type { HTMLAttributes } from 'vue';

interface Props {
  /** 単一画像モードの CMS 生 画像 URL（最適化はこのコンポーネントが行うため、呼び出し側は生 URL を渡す） */
  imageUrl?: string;
  /** 単一画像モードの Cloudflare 画像変換オプション（width / height / quality / fit / format）。format 既定は 'auto' */
  optimizedImageOptions?: OptimizeImageOptions;
  /** SP/PC 出し分けモードの SP 用 生 URL（pcImageUrl と両方指定で <picture> 描画） */
  spImageUrl?: string;
  /** SP/PC 出し分けモードの PC 用 生 URL（spImageUrl と両方指定で <picture> 描画） */
  pcImageUrl?: string;
  /** SP 用の画像変換オプション（width は SP 解像度に合わせる） */
  spOptimizedImageOptions?: OptimizeImageOptions;
  /** PC 用の画像変換オプション（width は PC 解像度に合わせる） */
  pcOptimizedImageOptions?: OptimizeImageOptions;
  alt?: string;
  class?: HTMLAttributes['class'];
  loading?: 'lazy' | 'eager';
  /** LCP 候補のヒーロー等で先読み優先度を上げたいとき 'high' を指定する */
  fetchpriority?: 'high' | 'low' | 'auto';
  /**
   * LCP 候補を最優先で先読みするため <head> に <link rel=preload as=image> を登録する。
   * 描画する最適化URLと同一URLを使うため先読みが確実に一致する（picture モードでは SP/PC を media で出し分けて2本登録）。
   */
  preload?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  imageUrl: undefined,
  optimizedImageOptions: () => ({}),
  spImageUrl: undefined,
  pcImageUrl: undefined,
  spOptimizedImageOptions: () => ({}),
  pcOptimizedImageOptions: () => ({}),
  alt: '',
  class: undefined,
  loading: undefined,
  fetchpriority: undefined,
  preload: false,
});

// inject は setup で解決し、computed の再評価で inject を踏まないようにする（再評価がレンダリング外で走ると 500 になるため）
const getOptimizedImageUrl = useOptimizedImageUrl();
// SP/PC 両方が渡されたときだけ picture 出し分けモードにする
const isPictureMode = computed(() => props.spImageUrl !== undefined && props.pcImageUrl !== undefined);
const optimizedImageUrl = computed(() => getOptimizedImageUrl(props.imageUrl ?? '', props.optimizedImageOptions));
const optimizedSpImageUrl = computed(() => getOptimizedImageUrl(props.spImageUrl ?? '', props.spOptimizedImageOptions));
const optimizedPcImageUrl = computed(() => getOptimizedImageUrl(props.pcImageUrl ?? '', props.pcOptimizedImageOptions));

// preload 指定時のみ <head> に preload link を登録する。
// 描画と同じ最適化URLを使い、picture の media 境界(767/768)に合わせて SP/PC を出し分ける。
// URL 未確定（CMS ロード前）の間は空配列で、確定後にリアクティブに反映される。
if (props.preload) {
  useHead({
    link: computed(() => {
      const links: { rel: 'preload'; as: 'image'; href: string; media?: string; fetchpriority: 'high' }[] = [];
      if (isPictureMode.value) {
        if (optimizedSpImageUrl.value) {
          links.push({ rel: 'preload', as: 'image', href: optimizedSpImageUrl.value, media: '(max-width: 767px)', fetchpriority: 'high' });
        }
        if (optimizedPcImageUrl.value) {
          links.push({ rel: 'preload', as: 'image', href: optimizedPcImageUrl.value, media: '(min-width: 768px)', fetchpriority: 'high' });
        }
      } else if (optimizedImageUrl.value) {
        links.push({ rel: 'preload', as: 'image', href: optimizedImageUrl.value, fetchpriority: 'high' });
      }
      return links;
    }),
  });
}
</script>
